import { escapeRegExp } from 'lodash';

/**
 * BDL-2808 AC3: Tailwind v3's PostCSS/JIT integration has no independent
 * file watcher and depends on an in-process mtime cache that is only ever
 * cleared by a full dev-server restart. A worktree dev server can therefore
 * silently serve CSS that is missing a rule for a class the source actually
 * declares -- the page renders 90% correctly (every class that generated
 * before still works) so nothing looks broken and nothing errors. See
 * managed-apps/business-coach/CLAUDE.md, "A Tailwind ARBITRARY-VALUE class
 * that landed AFTER the dev server started...".
 *
 * This is an architectural limitation of Tailwind v3's PostCSS-JIT model,
 * not a bug this repo can patch away by making the watcher "just work" --
 * so the fallback is a loud, mechanized check: never trust that a layout
 * LOOKS right, always assert the class you wrote is actually present in the
 * served stylesheet, using a known-stable class as a control so an absent
 * control (wrong stylesheet / empty build) can't be misread as "not stale".
 */

// Same character class `cssesc` (the library postcss-selector-parser --
// and therefore Tailwind's own escapeClassName -- uses internally, via
// `cssesc(value, { isIdentifier: true })`) treats as needing a single
// backslash escape.
const SINGLE_ESCAPE_CHARS = /[ -,./:-@[\]^`{-~]/;
// Cleanup pass: a `\HEX` escape only needs its trailing space when the
// NEXT character would otherwise extend the hex sequence (another hex
// digit) or is whitespace -- e.g. `2xl:hidden` -> `\32xl\:hidden` (no
// space, 'x' isn't hex) but `2ab` -> `\32 ab` (space needed, 'a' is hex).
const EXCESSIVE_HEX_ESCAPE_SPACES =
	/(^|\\+)?(\\[A-F0-9]{1,6})\x20(?![a-fA-F0-9\x20])/g;

/**
 * Escapes a Tailwind class name into the identifier Tailwind itself emits
 * as a CSS selector. Ported by hand from `cssesc`'s `isIdentifier: true`
 * algorithm (the actual library Tailwind uses internally, via
 * postcss-selector-parser's ClassName node) rather than requiring it
 * directly, so this stays a small, dependency-free function safe to
 * bundle into dev-only browser code without an `npm install`. Cross-
 * checked byte-for-byte against tailwindcss's own escapeClassName in
 * tailwindBuildFreshness.test.ts across representative class shapes
 * (colons, brackets, slashes, leading digits, leading dashes for negative
 * utilities like `-mt-4`) -- do not hand-edit this without re-running
 * that cross-check.
 */
export function escapeTailwindClassName(className: string): string {
	const firstChar = className.charAt(0);
	let output = '';

	for (let index = 0; index < className.length; index += 1) {
		const character = className.charAt(index);
		const codePoint = character.charCodeAt(0);

		if (codePoint < 0x20 || codePoint > 0x7e) {
			// Non-printable-ASCII / non-ASCII. Tailwind class names are
			// ASCII in practice, so surrogate-pair handling is
			// intentionally omitted here (matches the common case, not
			// the full cssesc astral-character path).
			output += `\\${codePoint.toString(16).toUpperCase()} `;
		} else if (
			character === '\\' ||
			SINGLE_ESCAPE_CHARS.test(character)
		) {
			output += `\\${character}`;
		} else {
			output += character;
		}
	}

	if (/^-[-\d]/.test(output)) {
		// Leading dash followed by another dash or digit (negative
		// utilities, e.g. `-mt-4` or `--1`): escape just the dash.
		output = `\\-${output.slice(1)}`;
	} else if (/\d/.test(firstChar)) {
		// Leading digit (e.g. `2xl:hidden`): CSS identifiers can't start
		// with a digit, so escape it as its hex codepoint. Digits 0-9 all
		// have ASCII hex codes of the form `3<digit>`, so `\3` + the
		// literal digit character is the hex escape.
		output = `\\3${firstChar} ${output.slice(1)}`;
	}

	output = output.replace(
		EXCESSIVE_HEX_ESCAPE_SPACES,
		(full: string, backslashes: string | undefined, hex: string) => {
			if (backslashes && backslashes.length % 2) {
				// Preceded by an odd number of backslashes -- the space
				// isn't safe to remove.
				return full;
			}
			return (backslashes || '') + hex;
		},
	);

	// Tailwind's real escapeClassName runs one more pass after cssesc:
	// tailwindcss/lib/util/escapeCommas.js rewrites a backslash-escaped
	// comma into its hex form (`\,` -> `\2c `). Arbitrary-value classes
	// with a comma exist in this repo (e.g. `grid-cols-[200px,1fr]`), and
	// without this step this escaper's output diverges from what Tailwind
	// actually emits -- silently turning a perfectly fresh build into a
	// false "STALE" report for any such class.
	output = output.replace(/\\,/g, '\\2c ');

	return output;
}

/** The full selector Tailwind generates for a given class, e.g. `.mr-\[913px\]`. */
export function buildTailwindSelectorFragment(className: string): string {
	return `.${escapeTailwindClassName(className)}`;
}

/**
 * True only if `className`'s OWN Tailwind selector is present in `cssText`
 * -- not merely present as a substring of some other, longer generated
 * class. Tailwind's spacing/sizing scales routinely produce class pairs
 * like `mr-1` / `mr-10` where a naive `cssText.includes('.mr-1')` would
 * false-positive against `.mr-10{...}`, silently defeating the whole check.
 */
export function isTailwindClassInCss(
	cssText: string,
	className: string,
): boolean {
	const fragment = buildTailwindSelectorFragment(className);
	const boundedPattern = new RegExp(
		`${escapeRegExp(fragment)}(?![a-zA-Z0-9_-])`,
	);
	return boundedPattern.test(cssText);
}

export interface TailwindBuildFreshnessResult {
	/** False whenever the build is confirmed stale OR the check itself couldn't run. */
	fresh: boolean;
	/** False when the control class was also absent -- the check is inconclusive, not "not stale". */
	controlValid: boolean;
	reason: string;
}

/**
 * The discriminating two-class control documented in CLAUDE.md: check a
 * class you're actually verifying (`candidateClass`) alongside one you
 * KNOW should already be generated (`knownStableClass`, e.g. a class used
 * elsewhere in the app since before the dev server booted). Control
 * present + candidate absent is the actual bug this ticket is about --
 * a silently partial Tailwind build. Control absent means the check can't
 * tell stale from broken (wrong stylesheet, empty build) and must say so
 * rather than reporting a false "fresh".
 */
export function checkTailwindBuildFreshness({
	cssText,
	candidateClass,
	knownStableClass,
}: {
	cssText: string;
	candidateClass: string;
	knownStableClass: string;
}): TailwindBuildFreshnessResult {
	if (!isTailwindClassInCss(cssText, knownStableClass)) {
		return {
			fresh: false,
			controlValid: false,
			reason: `Control class "${knownStableClass}" is ALSO absent from the served CSS -- this check cannot distinguish a stale Tailwind build from an empty/wrong stylesheet. Re-run against the actual page stylesheet, or pick a control class you're certain is already generated.`,
		};
	}

	if (!isTailwindClassInCss(cssText, candidateClass)) {
		return {
			fresh: false,
			controlValid: true,
			reason: `STALE TAILWIND BUILD: control class "${knownStableClass}" is present but "${candidateClass}" is not. Restart the dev server -- Tailwind's JIT engine only clears its in-process mtime cache on a full restart (BDL-2808).`,
		};
	}

	return {
		fresh: true,
		controlValid: true,
		reason: `Both "${knownStableClass}" (control) and "${candidateClass}" (candidate) are present in the served CSS.`,
	};
}
